"use client";

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { LucideIcon } from "lucide-react";
import {
  Check,
  ChevronDown,
  LoaderCircle,
  Mail,
  Moon,
  MoreVertical,
  RefreshCw,
  Search,
  ShieldCheck,
  Sun,
  UserPlus,
  Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PluginConnectionsPanel } from "./plugin-connections-panel";
import { EnterpriseContextMenu } from "./enterprise-context-menu";
import { useRunyiValueSync } from "../lib/runyi-value-sync";
import { MemberProjectPanel } from "./member-project-panel";
import { MemberDataAuthPanel, type MemberProjectRuntimeProfile } from "./member-data-auth-panel";
import { MemberManagementPanel } from "./member-management-panel";
import { M4ControlTowerPanel } from "./m4-control-tower-panel";
import { AccountSetupPanel } from "./account-setup-panel";
import { UniversalAccountsSetupPanel } from "./universal-accounts-setup-panel";
import { RunyiBotWorkspacePanel } from "./runyi-bot-workspace-panel";
import { EnterpriseApprovalWorkspace } from "./enterprise-approval-workspace";
import { useRunyiBackTrail } from "@/lib/use-runyi-back-trail";
import { EnterpriseControlTowerPanel } from "./enterprise-control-tower-panel";
import { RunyiUiCoordinator, resolveRunyiUiLayoutContract, useRunyiUiProjection } from "./runyi-ui-coordinator";

type Appearance = "light" | "dark";
type Health = {
  configured: boolean;
  connected: boolean;
  signedIn?: boolean;
  movieCount?: number;
  conceptRunCount?: number;
  schemaVersion?: string;
  privateAccount?: boolean;
  role?: "OWNER_ADMIN" | "ADMIN" | "MEMBER";
  activeUserCount?: number;
  maximumUsers?: number;
  error?: string;
};

type AllowedUser = {
  email: string;
  role: "OWNER_ADMIN" | "ADMIN" | "MEMBER";
  active: boolean;
  user_id?: string | null;
  permissions?: Record<string, boolean>;
  accessible_user_ids?: string[];
  joined: boolean;
  created_at?: string | null;
  activated_at?: string | null;
  last_seen_at?: string | null;
};

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="grid min-w-0 gap-2 text-sm text-foreground/70"><span className="text-xs font-semibold uppercase tracking-wider text-foreground/38">{label}</span>{children}</label>;
}

type SettingsGroupKey =
  | "account"
  | "activation"
  | "backend"
  | "infrastructure"
  | "operations"
  | "advanced";

function SettingsGroup({
  title,
  description,
  icon: Icon,
  status,
  open,
  hidden,
  onToggle,
  children,
}: {
  title: string;
  description: string;
  icon: LucideIcon;
  status?: string;
  open: boolean;
  hidden?: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  if (hidden) return null;
  return (
    <section className="scroll-mt-28 overflow-visible rounded-2xl bg-foreground/[0.018]">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition hover:bg-foreground/[0.025] sm:px-4"
        aria-expanded={open}
      >
        <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-primary/[0.08] text-primary">
          <Icon className="size-3.5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold">{title}</span>
          <span className="mt-0.5 block truncate text-[10px] text-foreground/38">{description}</span>
        </span>
        {status && (
          <Badge className="shrink-0 bg-foreground/[0.055] text-[9px] font-semibold text-foreground/58">
            {status}
          </Badge>
        )}
        <ChevronDown className={`size-3.5 shrink-0 text-foreground/30 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-foreground/[0.055] px-3 pb-4 pt-3 sm:px-4">
          {children}
        </div>
      )}
    </section>
  );
}

type RunyiSettingsNavigationEntry = {
  entry_key: string;
  display_name: string;
  description?: string | null;
  renderer_key?: string | null;
  target_screen_key: string;
  metadata?: Record<string, unknown>;
};

type RunyiRegisteredScreenRendererProps = {
  supabase: SupabaseClient | null;
  connected: boolean;
  isAdmin: boolean;
  payload: Record<string, any>;
  entry: RunyiSettingsNavigationEntry;
};

function runyiRegisteredScreenLabel(value: string) {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function RunyiUniversalScreenValue({ value, depth = 0 }: { value: any; depth?: number }) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-[11px] text-foreground/35">—</span>;
  }
  if (typeof value === "boolean") {
    return <span className="text-[11px] font-medium text-foreground/70">{value ? "Yes" : "No"}</span>;
  }
  if (typeof value !== "object") {
    return <span className="break-words text-[11px] text-foreground/70">{String(value)}</span>;
  }
  if (depth >= 3) {
    return (
      <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-foreground/[0.025] p-2 text-[10px] leading-relaxed text-foreground/55">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-[11px] text-foreground/35">None</span>;
    const visible = value.slice(0, 50);
    return (
      <div className="space-y-2">
        {visible.map((item, index) => (
          <div key={index} className="min-w-0 rounded-xl bg-foreground/[0.025] p-2.5">
            <RunyiUniversalScreenValue value={item} depth={depth + 1} />
          </div>
        ))}
        {value.length > visible.length ? (
          <p className="px-1 text-[10px] text-foreground/35">{value.length - visible.length} more</p>
        ) : null}
      </div>
    );
  }
  const entries = Object.entries(value as Record<string, any>);
  if (entries.length === 0) return <span className="text-[11px] text-foreground/35">None</span>;
  return (
    <div className="space-y-2">
      {entries.map(([key, item]) => (
        <div key={key} className="min-w-0 rounded-xl bg-foreground/[0.018] px-2.5 py-2">
          <p className="mb-1 text-[9px] font-semibold uppercase tracking-[0.08em] text-foreground/35">
            {runyiRegisteredScreenLabel(key)}
          </p>
          <RunyiUniversalScreenValue value={item} depth={depth + 1} />
        </div>
      ))}
    </div>
  );
}

function RunyiUniversalRegisteredScreenRenderer({ payload }: RunyiRegisteredScreenRendererProps) {
  const sections = Object.entries(payload).filter(
    ([key]) => !["screen", "title", "render_contract"].includes(key),
  );
  return (
    <div className="min-w-0 space-y-3">
      {sections.map(([key, value]) => (
        <section key={key} className="min-w-0 rounded-2xl bg-foreground/[0.025] p-3">
          <div className="mb-2 flex min-w-0 items-center justify-between gap-2">
            <p className="min-w-0 truncate text-[11px] font-semibold text-foreground/70">
              {runyiRegisteredScreenLabel(key)}
            </p>
          </div>
          <RunyiUniversalScreenValue value={value} />
        </section>
      ))}
      {sections.length === 0 ? (
        <div className="rounded-2xl bg-foreground/[0.025] px-4 py-6 text-xs text-foreground/45">
          RUNYI resolved this screen. No displayable data is currently registered.
        </div>
      ) : null}
    </div>
  );
}

const RUNYI_REGISTERED_SCREEN_RENDERERS: Record<string, (props: RunyiRegisteredScreenRendererProps) => ReactNode> = {
  RUNYI_UNIVERSAL_ACCOUNTS_SETUP_RENDERER_V1: ({ supabase, connected }) => (
    <UniversalAccountsSetupPanel supabase={supabase} connected={connected} />
  ),
  RUNYI_BOTS_WORKSPACE_RENDERER_V1: ({ payload, supabase, connected, isAdmin }) => (
    <RunyiBotWorkspacePanel payload={payload} supabase={supabase} connected={connected} isAdmin={isAdmin} />
  ),
};

const RUNYI_REGISTERED_ARCHITECTURE_RENDERERS: Record<string, (props: RunyiRegisteredScreenRendererProps) => ReactNode> = {
  RUNYI_UNIVERSAL_BOT_WORKSPACE_V1: ({ payload, supabase, connected, isAdmin }) => (
    <RunyiBotWorkspacePanel payload={payload} supabase={supabase} connected={connected} isAdmin={isAdmin} />
  ),
  UNIVERSAL_ACCOUNTS_SETUP_V1: ({ supabase, connected }) => (
    <UniversalAccountsSetupPanel supabase={supabase} connected={connected} />
  ),
};

function RunyiRegisteredSettingsScreen({
  entry,
  supabase,
  connected,
  isAdmin,
  onBack,
}: {
  entry: RunyiSettingsNavigationEntry;
  supabase: SupabaseClient | null;
  connected: boolean;
  isAdmin: boolean;
  onBack: () => void;
}) {
  const projection = useRunyiUiProjection({
    supabase,
    connected,
    screenKey: entry.target_screen_key,
  });
  const screenPayload = (projection?.payload ?? null) as Record<string, any> | null;
  const screenError = projection?.last_error ? String(projection.last_error) : null;

  const requiredRendererContract = String(
    screenPayload?.renderer_contract?.required_contract
      ?? screenPayload?.bot_workspace_ui_contract?.renderer_contract_version
      ?? "",
  );
  const rendererKey = requiredRendererContract || String(screenPayload?.render_contract?.renderer_key ?? "");
  const architectureKey = String(entry.metadata?.architecture ?? "");
  const uiLayoutContract = resolveRunyiUiLayoutContract({
    payload: screenPayload,
    rendererKey,
    architectureKey,
    screenKey: entry.target_screen_key,
  });
  const exactRenderer = rendererKey ? RUNYI_REGISTERED_SCREEN_RENDERERS[rendererKey] : undefined;
  const renderer = exactRenderer ?? (!requiredRendererContract ? RUNYI_REGISTERED_ARCHITECTURE_RENDERERS[architectureKey] : undefined);
  const rendererUnsupported = Boolean(requiredRendererContract && !exactRenderer);
  const renderedScreen = rendererUnsupported
    ? (
      <div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 p-5">
        <p className="text-sm font-semibold">UI UPDATE REQUIRED</p>
        <p className="mt-1 text-xs text-foreground/55">
          This RUNYI screen requires renderer {requiredRendererContract}. The installed client will not render a stale or incompatible workspace.
        </p>
      </div>
    )
    : renderer
      ? renderer({ supabase, connected, isAdmin, payload: screenPayload ?? {}, entry })
      : <RunyiUniversalRegisteredScreenRenderer supabase={supabase} connected={connected} isAdmin={isAdmin} payload={screenPayload ?? {}} entry={entry} />;

  const screenHeader = (
    <div className="flex items-center gap-2 rounded-2xl bg-foreground/[0.025] p-2">
      <Button type="button" variant="ghost" onClick={onBack} className="h-9 rounded-xl px-3 text-xs">
        <ChevronDown className="size-3.5 rotate-90" /> Back
      </Button>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{entry.display_name}</p>
        {entry.description ? <p className="truncate text-[10px] text-foreground/38">{entry.description}</p> : null}
      </div>
    </div>
  );

  return (
    <RunyiUiCoordinator contract={uiLayoutContract} header={screenHeader}>
      {screenError ? (
        <div className="rounded-xl bg-amber-300/[0.08] px-3 py-2.5 text-xs text-amber-700 dark:text-amber-100">
          RUNYI is updating this screen in the background. The last known good workspace remains available.
        </div>
      ) : null}
      {renderedScreen}
    </RunyiUiCoordinator>
  );
}

export function SettingsPanel({
  health,
  supabase,
  loading,
  authLoading,
  signedInEmail,
  notice,
  appearance,
  onAppearanceChange,
  onSendCode,
  onVerifyCode,
  onSignOut,
  onRefresh,
  memberProjectProfile,
  memberSupabase,
  memberDataSupabase,
  tenantSignedInEmail,
  onTenantAuthChanged,
  onTenantAccessTokenChanged,
  onRefreshMemberProjectProfile,
}: {
  health: Health;
  supabase: SupabaseClient | null;
  loading: boolean;
  authLoading: boolean;
  signedInEmail: string | null;
  notice: string | null;
  appearance: Appearance;
  onAppearanceChange: (appearance: Appearance) => void;
  onSendCode: (email: string) => void;
  onVerifyCode: (email: string, token: string) => void;
  onSignOut: () => void;
  onRefresh: () => void;
  memberProjectProfile: MemberProjectRuntimeProfile | null;
  memberSupabase: SupabaseClient | null;
  memberDataSupabase: SupabaseClient | null;
  tenantSignedInEmail: string | null;
  onTenantAuthChanged: (email: string | null) => void;
  onTenantAccessTokenChanged: (accessToken: string | null) => void;
  onRefreshMemberProjectProfile: () => Promise<void>;
}) {
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [enterpriseStatus, setEnterpriseStatus] = useState<Record<string, any> | null>(null);
  const [settingsSearch, setSettingsSearch] = useState("");
  const [oneTimeBrowserLoading, setOneTimeBrowserLoading] = useState(false);
  const [oneTimeBrowserMessage, setOneTimeBrowserMessage] = useState<string | null>(null);
  const [oneTimeBrowserOptions, setOneTimeBrowserOptions] = useState<Array<{
    setup_id: string;
    display_name?: string | null;
    provider_key?: string | null;
    target_account_ref?: string | null;
    target_member_email?: string | null;
  }>>([]);
  const [openGroups, setOpenGroups] = useState<Record<SettingsGroupKey, boolean>>({
    account: true,
    activation: true,
    backend: false,
    infrastructure: false,
    operations: false,
    advanced: false,
  });
  const [settingsNavigationEntries, setSettingsNavigationEntries] = useState<RunyiSettingsNavigationEntry[]>([]);
  const [activeSettingsScreen, setActiveSettingsScreen] = useState<RunyiSettingsNavigationEntry | null>(null);

  const loadSettingsNavigation = useCallback(async () => {
    if (!supabase || !health.connected) return;
    try {
      const { data, error } = await supabase.functions.invoke("runyi-ui-gateway", {
        body: { screen: "SETTINGS", mode: "read" },
      });
      if (error) throw error;
      const payload = (data?.payload ?? data ?? {}) as Record<string, any>;
      const entries = Array.isArray(payload?.navigation_entries)
        ? payload.navigation_entries.filter((entry: any) => entry && typeof entry.entry_key === "string" && typeof entry.target_screen_key === "string")
        : [];
      setSettingsNavigationEntries(entries as RunyiSettingsNavigationEntry[]);
    } catch {
      // Preserve the last resolved navigation state; the central UI gateway owns recovery.
    }
  }, [health.connected, supabase]);

  useEffect(() => {
    void loadSettingsNavigation();
  }, [loadSettingsNavigation]);

  useEffect(() => {
    const onBack = (event: Event) => {
      if (event.defaultPrevented) return;
      if (activeSettingsScreen) {
        event.preventDefault();
        setActiveSettingsScreen(null);
        return;
      }
      if (!settingsSearch) return;
      event.preventDefault();
      setSettingsSearch("");
    };
    window.addEventListener("runyi:request-back", onBack);
    return () => window.removeEventListener("runyi:request-back", onBack);
  }, [activeSettingsScreen, settingsSearch]);

  useRunyiBackTrail(openGroups, setOpenGroups);


  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw=window.sessionStorage.getItem("runyi:workspace-state:governance");
      if (!raw) return;
      const state=JSON.parse(raw) as {settingsSearch?:string;openGroups?:Record<SettingsGroupKey,boolean>};
      if (typeof state.settingsSearch === "string") setSettingsSearch(state.settingsSearch);
      if (state.openGroups) setOpenGroups((current) => ({...current,...state.openGroups}));
    } catch {
      // Invalid local Settings workspace state is ignored.
    }
  },[]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem("runyi:workspace-state:governance",JSON.stringify({settingsSearch,openGroups}));
    } catch {
      // Back-state persistence is best effort only.
    }
  },[openGroups,settingsSearch]);

  const settingsSync = useRunyiValueSync({
    supabase, connected: Boolean(supabase && signedInEmail && health.connected),
    domains: ["identity", "infrastructure", "catalogue", "applications"],
    onRefresh: async () => {
      if (!supabase) return;
      const { data, error } = await supabase.rpc("runyi_enterprise_connection_status");
      if (error) throw error;
      setEnterpriseStatus((data ?? null) as Record<string, any> | null);
    },
    intervalMs: 60_000,
  });


  const emailVerified = Boolean(signedInEmail);
  const isOwner = health.role === "OWNER_ADMIN";
  const isSetupAdmin = health.role === "OWNER_ADMIN" || health.role === "ADMIN";
  const memberProjectVerified = enterpriseStatus?.project?.verified === true
    || (memberProjectProfile?.configured === true && memberProjectProfile?.connection_status === "VERIFIED");
  const projectVerified = isOwner ? Boolean(health.connected) : memberProjectVerified;
  const tenantReady = isOwner
    ? Boolean(emailVerified && health.connected)
    : enterpriseStatus?.tenant?.enrolled === true || Boolean(tenantSignedInEmail);
  const managementReady = isOwner
    ? Boolean(health.connected)
    : enterpriseStatus?.management?.authorized === true;
  const controlTowerReady = isOwner
    ? Boolean(health.connected)
    : enterpriseStatus?.control_tower?.ready === true
      || memberProjectProfile?.schema_version === "member-control-tower-v4";
  const m4Ready = enterpriseStatus?.m4?.ready === true || Boolean(health.connected);
  const backendReady = isOwner
    ? Boolean(emailVerified && health.connected && controlTowerReady && m4Ready)
    : enterpriseStatus?.ready === true
      || Boolean(health.connected && memberProjectVerified && managementReady);

  const settingsQuery = settingsSearch.trim().toLocaleLowerCase();
  const visibleNavigationEntries = settingsNavigationEntries.filter((entry) => {
    if (!settingsQuery) return true;
    return [entry.display_name, entry.description ?? "", entry.entry_key, entry.target_screen_key]
      .join(" ")
      .toLocaleLowerCase()
      .includes(settingsQuery);
  });
  const settingsIndex: Record<SettingsGroupKey, string> = {
    account: "account appearance theme white black identity email sign in sign out profile",
    activation: "enterprise accounts account onboarding approval permissions roles maintenance first time setup activation otp code link auto email supabase authorization governed support session finish activation",
    backend: "backend connections database member project tenant control tower m4 automatic connection state health",
    infrastructure: "infrastructure one-time browser authorization provider github supabase browser connect authentication",
    operations: "operations governance enterprise control tower fleet health readiness knowledge deployment providers github cloudflare firebase supabase incidents provenance",
    advanced: "advanced technical member project data authentication plugins integrations m4 control tower database storage",
  };
  const groupVisible = (key: SettingsGroupKey) => !settingsQuery || settingsIndex[key].includes(settingsQuery);
  const groupOpen = (key: SettingsGroupKey) => settingsQuery ? groupVisible(key) : openGroups[key];
  const toggleGroup = (key: SettingsGroupKey) => {
    if (settingsQuery) return;
    setOpenGroups((current) => ({ ...current, [key]: !current[key] }));
  };
  const visibleGroupCount = (Object.keys(settingsIndex) as SettingsGroupKey[]).filter(groupVisible).length;
  const setAllGroups = (value: boolean) => {
    setOpenGroups({
      account: value,
      activation: value,
      backend: value,
      infrastructure: value,
      operations: value,
      advanced: value,
    });
  };

  const launchOneTimeBrowserUrl = useCallback((url: string) => {
    type OneTimeBrowserBridge = {
      openOneTimeInfrastructureBrowser?: (value: string) => boolean;
      openOneTimeOAuth?: (value: string) => boolean;
    };
    const bridgeWindow = window as typeof window & {
      RunyiAndroid?: OneTimeBrowserBridge;
      RUNYINative?: OneTimeBrowserBridge;
    };
    const nativeBridge = bridgeWindow.RunyiAndroid ?? bridgeWindow.RUNYINative;

    if (nativeBridge) {
      const openNative = nativeBridge.openOneTimeInfrastructureBrowser
        ?? nativeBridge.openOneTimeOAuth;
      if (typeof openNative !== "function") {
        throw new Error("The RUNYI Android shell does not expose the One-Time Browser bridge.");
      }
      const opened = openNative.call(nativeBridge, url);
      if (opened !== true) {
        throw new Error("RUNYI could not open an isolated one-time browser on this device.");
      }
      return;
    }

    window.location.assign(url);
  }, []);

  const openOneTimeBrowser = useCallback(async (setupId?: string) => {
    if (!supabase) {
      setOneTimeBrowserMessage("RUNYI session is reconnecting. Refresh enterprise state and try again.");
      return;
    }
    setOneTimeBrowserLoading(true);
    setOneTimeBrowserMessage(null);
    try {
      let selectedSetupId = setupId ?? "";
      if (!selectedSetupId) {
        const { data: statusData, error: statusError } = await supabase.functions.invoke(
          "runyi-infrastructure-browser-entry",
          { body: { action: "status" } },
        );
        if (statusError) throw new Error(statusError.message);
        if (statusData?.error) throw new Error(String(statusData.detail ?? statusData.error));
        const options = Array.isArray(statusData?.options) ? statusData.options : [];
        const pendingCount = Number(statusData?.pending_count ?? options.length ?? 0);
        if (pendingCount === 0) {
          setOneTimeBrowserOptions([]);
          setOneTimeBrowserMessage("No provider authorization is waiting. Start or resume an item in New Infrastructure first.");
          return;
        }
        if (pendingCount > 1) {
          setOneTimeBrowserOptions(options);
          setOneTimeBrowserMessage("Choose the infrastructure authorization to open.");
          return;
        }
        selectedSetupId = String(options[0]?.setup_id ?? "");
      }
      const { data, error } = await supabase.functions.invoke(
        "runyi-infrastructure-browser-entry",
        { body: { action: "open", ...(selectedSetupId ? { setup_id: selectedSetupId } : {}) } },
      );
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(String(data.detail ?? data.error));
      const url = String(data?.authorization_url ?? "");
      if (!url) {
        setOneTimeBrowserMessage(
          data?.state === "NO_PENDING_AUTHORIZATION"
            ? "No provider authorization is waiting. Start or resume an item in New Infrastructure first."
            : "RUNYI did not return a one-time browser launch.",
        );
        return;
      }
      setOneTimeBrowserOptions([]);
      setOneTimeBrowserMessage("Opening the isolated RUNYI one-time browser…");
      launchOneTimeBrowserUrl(url);
    } catch (error) {
      setOneTimeBrowserMessage(error instanceof Error ? error.message : "RUNYI could not open the one-time browser.");
    } finally {
      setOneTimeBrowserLoading(false);
    }
  }, [launchOneTimeBrowserUrl, supabase]);

  if (activeSettingsScreen) {
    return (
      <RunyiRegisteredSettingsScreen
        entry={activeSettingsScreen}
        supabase={supabase}
        connected={health.connected}
        isAdmin={health.role === "OWNER_ADMIN" || health.role === "ADMIN"}
        onBack={() => setActiveSettingsScreen(null)}
      />
    );
  }

  return (
    <div className="runyi-settings-compact mx-auto w-full min-w-0 max-w-none space-y-4 overflow-x-hidden px-1 pb-[calc(8rem+env(safe-area-inset-bottom))] pt-2 sm:px-2">
      <header className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-foreground/38">RUNYI enterprise</p>
            <h1 className="mt-1 text-2xl font-semibold tracking-[-0.03em] sm:text-3xl">Settings</h1>
            <p className="mt-1 max-w-2xl text-[11px] leading-5 text-foreground/42 sm:text-xs">
              Search and manage identity, enterprise backends, governance, access and technical controls from one compact workspace.
            </p>
          </div>

          <EnterpriseContextMenu label="Settings actions" title="Settings actions">
              <button
                type="button"
                onClick={(event) => {
                                    onRefresh();
                }}
                className="flex h-9 w-full items-center gap-2 rounded-lg px-2.5 text-left text-xs text-foreground/75 hover:bg-foreground/8"
              >
                <RefreshCw className="size-3.5" /> Refresh enterprise state
              </button>
              <button
                type="button"
                onClick={(event) => {
                                    setAllGroups(true);
                }}
                className="flex h-9 w-full items-center rounded-lg px-2.5 text-left text-xs text-foreground/75 hover:bg-foreground/8"
              >
                Expand all sections
              </button>
              <button
                type="button"
                onClick={(event) => {
                                    setAllGroups(false);
                }}
                className="flex h-9 w-full items-center rounded-lg px-2.5 text-left text-xs text-foreground/75 hover:bg-foreground/8"
              >
                Collapse all sections
              </button>
              {emailVerified && (
                <button
                  type="button"
                  disabled={authLoading}
                  onClick={(event) => {
                                        onSignOut();
                  }}
                  className="mt-1 flex h-9 w-full items-center rounded-lg border-t border-foreground/8 px-2.5 text-left text-xs text-foreground/65 hover:bg-foreground/8 disabled:opacity-40"
                >
                  Sign out
                </button>
              )}
          </EnterpriseContextMenu>
        </div>

        <div className="rounded-2xl bg-foreground/[0.025] p-2">
          <span className="sr-only" role="status">Enterprise Settings value synchronization: {settingsSync.state}</span>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-foreground/30" />
            <Input
              type="search"
              inputMode="search"
              autoComplete="off"
              spellCheck={false}
              value={settingsSearch}
              onChange={(event) => setSettingsSearch(event.target.value)}
              placeholder="Search Settings — users, Firebase, roles, deployment…"
              aria-label="Search RUNYI Settings"
              className="h-10 border-0 bg-background/75 pl-9 pr-3 text-sm shadow-none placeholder:text-[11px] placeholder:text-foreground/28 focus-visible:ring-1 focus-visible:ring-primary/20"
            />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5 px-1">
            <Badge className={emailVerified ? "bg-emerald-400/10 text-emerald-500 dark:text-emerald-300" : "bg-foreground/[0.055] text-foreground/50"}>
              {emailVerified ? "IDENTITY VERIFIED" : "IDENTITY PENDING"}
            </Badge>
            <Badge className={backendReady ? "bg-emerald-400/10 text-emerald-500 dark:text-emerald-300" : "bg-amber-400/10 text-amber-600 dark:text-amber-200"}>
              {backendReady ? "BACKEND READY" : "BACKEND CHECK"}
            </Badge>
            <Badge className="bg-foreground/[0.055] text-foreground/55">
              {health.role === "OWNER_ADMIN" ? "OWNER ADMIN" : health.role === "ADMIN" ? "ADMIN" : "MEMBER"}
            </Badge>
            {settingsQuery && (
              <span className="ml-auto text-[10px] text-foreground/38">{visibleGroupCount} matching sections</span>
            )}
          </div>
        </div>
      </header>

      {notice && (
        <div className="rounded-xl bg-amber-300/[0.08] px-3 py-2.5 text-xs leading-5 text-amber-700 dark:text-amber-100">
          {notice}
        </div>
      )}

      {visibleNavigationEntries.length > 0 && (
        <section className="space-y-2 rounded-2xl bg-foreground/[0.018] p-2">
          <div className="px-1 pb-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">RUNYI functions</p>
            <p className="mt-1 text-[10px] text-foreground/38">Registered Settings destinations resolved by the central UI registry.</p>
          </div>
          {visibleNavigationEntries.map((entry) => (
            <button
              key={entry.entry_key}
              type="button"
              onClick={() => setActiveSettingsScreen(entry)}
              className="flex w-full items-center gap-3 rounded-xl bg-background/55 px-3 py-3 text-left transition hover:bg-foreground/[0.04]"
            >
              <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary/[0.08] text-primary"><ShieldCheck className="size-3.5" /></span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-xs font-semibold">{entry.display_name}</span>
                {entry.description ? <span className="mt-0.5 block truncate text-[10px] text-foreground/38">{entry.description}</span> : null}
              </span>
              <ChevronDown className="size-3.5 shrink-0 -rotate-90 text-foreground/30" />
            </button>
          ))}
        </section>
      )}

      <div className="space-y-2">
        <SettingsGroup
          title="Account & Appearance"
          description="Profile, session and visual preferences"
          icon={ShieldCheck}
          status={emailVerified ? "VERIFIED" : "SIGN IN"}
          open={groupOpen("account")}
          hidden={!groupVisible("account")}
          onToggle={() => toggleGroup("account")}
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 rounded-xl bg-background/55 px-3 py-3">
              <div>
                <p className="text-xs font-semibold">Theme</p>
                <p className="mt-0.5 text-[10px] text-foreground/38">Pure RUNYI white or black interface.</p>
              </div>
              <div className="inline-grid grid-cols-2 rounded-full bg-foreground/[0.045] p-0.5">
                <button
                  type="button"
                  onClick={() => onAppearanceChange("light")}
                  className={`flex h-8 items-center gap-1.5 rounded-full px-3 text-[11px] font-medium transition ${appearance === "light" ? "bg-background text-foreground shadow-sm" : "text-foreground/42"}`}
                >
                  <Sun className="size-3" /> White
                </button>
                <button
                  type="button"
                  onClick={() => onAppearanceChange("dark")}
                  className={`flex h-8 items-center gap-1.5 rounded-full px-3 text-[11px] font-medium transition ${appearance === "dark" ? "bg-background text-foreground shadow-sm" : "text-foreground/42"}`}
                >
                  <Moon className="size-3" /> Black
                </button>
              </div>
            </div>

            <div className="rounded-xl bg-background/55 px-3 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className={`grid size-7 shrink-0 place-items-center rounded-full ${emailVerified ? "bg-emerald-400/12 text-emerald-500 dark:text-emerald-300" : "bg-foreground/[0.06] text-foreground/55"}`}>
                  {emailVerified ? <Check className="size-3.5" /> : <Mail className="size-3.5" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold">RUNYI identity</p>
                  <p className="mt-0.5 truncate text-[10px] text-foreground/40">{signedInEmail ?? "Not signed in"}</p>
                </div>
                {emailVerified && <Badge className="bg-emerald-400/10 text-emerald-500 dark:text-emerald-300">ACTIVE</Badge>}
              </div>

              {!emailVerified && (
                <div className="mt-3 space-y-2">
                  <Input
                    type="email"
                    inputMode="email"
                    autoCapitalize="none"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="Approved email address"
                    className="h-10 border-0 bg-foreground/[0.035] px-3 text-[15px] shadow-none placeholder:text-[11px] placeholder:text-foreground/28 focus-visible:ring-1 focus-visible:ring-primary/20"
                  />
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                    <Input
                      value={verificationCode}
                      onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 8))}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="Email OTP"
                      className="h-10 min-w-0 border-0 bg-foreground/[0.035] px-3 text-[15px] shadow-none placeholder:text-[11px] placeholder:text-foreground/28 focus-visible:ring-1 focus-visible:ring-primary/20"
                    />
                    <Button
                      onClick={() => verificationCode.length >= 6 ? onVerifyCode(email, verificationCode) : onSendCode(email)}
                      disabled={authLoading || !supabase || !email.trim()}
                      className="h-10 rounded-xl px-4 text-xs font-semibold"
                    >
                      {authLoading ? <LoaderCircle className="size-3.5 animate-spin" /> : verificationCode.length >= 6 ? <ShieldCheck className="size-3.5" /> : <Mail className="size-3.5" />}
                      {verificationCode.length >= 6 ? "Verify" : "Send code"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </SettingsGroup>

        <SettingsGroup
          title="Enterprise Accounts"
          description="Account onboarding, approvals, authorization and maintenance"
          icon={Check}
          status={backendReady ? "READY" : "PENDING"}
          open={groupOpen("activation")}
          hidden={!groupVisible("activation")}
          onToggle={() => toggleGroup("activation")}
        >
          {!emailVerified ? (
            <p className="rounded-xl bg-background/55 px-3 py-3 text-xs leading-5 text-foreground/45">
              Verify the RUNYI account email under Account & Appearance to open the governed Enterprise Accounts workspace.
            </p>
          ) : (
            <div className="rounded-xl bg-background/55 p-3">
              <div className="flex items-start gap-3">
                <span className={`grid size-7 shrink-0 place-items-center rounded-full ${backendReady ? "bg-emerald-400/12 text-emerald-500 dark:text-emerald-300" : "bg-foreground/[0.06] text-foreground/55"}`}>
                  {backendReady ? <Check className="size-3.5" /> : "2"}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold">Authorize Supabase</p>
                  <p className="mt-1 text-[10px] leading-4 text-foreground/40">
                    RUNYI stores management authorization server-side and restores the correct tenant/project routing automatically.
                  </p>
                </div>
                <Badge className={backendReady ? "bg-emerald-400/10 text-emerald-500 dark:text-emerald-300" : "bg-amber-400/10 text-amber-600 dark:text-amber-200"}>
                  {backendReady ? "READY" : "PENDING"}
                </Badge>
              </div>

              <div className="mt-3">
                {isSetupAdmin ? (
                  <div className="space-y-3">
                    <div className="rounded-lg bg-foreground/[0.025] px-3 py-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold">RUNYI central control plane</p>
                          <p className="mt-1 text-[10px] leading-4 text-foreground/42">
                            Owner access uses the central RUNYI project. Member-project authorization remains tenant-scoped.
                          </p>
                        </div>
                        <Badge className={health.connected ? "bg-emerald-400/10 text-emerald-500 dark:text-emerald-300" : "bg-amber-400/10 text-amber-600 dark:text-amber-200"}>
                          {health.connected ? "CONNECTED" : "RECONNECTING"}
                        </Badge>
                      </div>
                    </div>

                    <EnterpriseApprovalWorkspace supabase={supabase} connected={health.connected} scope="account" />
                    {isOwner && (
                      <div className="min-w-0 rounded-xl border border-foreground/[0.055] bg-foreground/[0.018] p-3">
                        <p className="mb-2 text-xs font-semibold">Account authorization & maintenance</p>
                        <AdminUsersPanel supabase={supabase} />
                      </div>
                    )}
                    <div className="min-w-0 rounded-xl border border-foreground/[0.055] bg-foreground/[0.018] p-2 sm:p-3">
                      <div className="mb-2 px-1">
                        <p className="text-xs font-semibold">Governed account onboarding & recovery</p>
                        <p className="mt-1 text-[10px] leading-4 text-foreground/40">
                          Continue the selected account&apos;s Code, Link or Auto verification, first-use test, maintenance and activation here without leaving Enterprise Accounts.
                        </p>
                      </div>
                      <AccountSetupPanel
                        supabase={supabase}
                        actorEmail={signedInEmail}
                        onLogout={onSignOut}
                      />
                    </div>
                  </div>
                ) : (
                  <MemberManagementPanel
                    controlSupabase={supabase}
                    centralEmail={signedInEmail}
                    profile={memberProjectProfile}
                    compact
                  />
                )}
              </div>
            </div>
          )}
        </SettingsGroup>

        <SettingsGroup
          title="Backend & Connections"
          description="Database, tenant routing and runtime health"
          icon={RefreshCw}
          status={backendReady ? "ONLINE" : "CHECK"}
          open={groupOpen("backend")}
          hidden={!groupVisible("backend")}
          onToggle={() => toggleGroup("backend")}
        >
          {emailVerified ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold">Automatic connection state</p>
                  <p className="mt-0.5 text-[10px] text-foreground/38">Live enterprise runtime status.</p>
                </div>
                <Button variant="ghost" onClick={onRefresh} disabled={loading} className="h-8 rounded-full px-3 text-[10px] text-foreground/45">
                  {loading ? <LoaderCircle className="size-3 animate-spin" /> : <RefreshCw className="size-3" />}
                  Refresh
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-2 rounded-xl bg-background/55 p-3 sm:grid-cols-3">
                {(isOwner ? [
                  ["Identity", emailVerified],
                  ["Central project", projectVerified],
                  ["Database", health.connected],
                  ["Owner session", tenantReady],
                  ["Control Tower", controlTowerReady],
                  ["M4", m4Ready],
                ] : [
                  ["Identity", emailVerified],
                  ["Member project", memberProjectVerified],
                  ["Database", memberProjectVerified && health.connected],
                  ["Tenant session", tenantReady],
                  ["Control Tower", controlTowerReady],
                  ["M4", m4Ready],
                ]).map(([label, ok]) => (
                  <div key={String(label)} className="flex items-center gap-2 rounded-lg bg-foreground/[0.025] px-2.5 py-2">
                    <span className={`size-1.5 shrink-0 rounded-full ${ok ? "bg-emerald-400" : "bg-foreground/20"}`} />
                    <span className="truncate text-[10px] text-foreground/50">{String(label)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-foreground/42">Sign in to view enterprise backend state.</p>
          )}
        </SettingsGroup>

        <SettingsGroup
          title="Infrastructure"
          description="Universal account authentication and browser capability"
          icon={ShieldCheck}
          status="ACCOUNTS"
          open={groupOpen("infrastructure")}
          hidden={!groupVisible("infrastructure")}
          onToggle={() => toggleGroup("infrastructure")}
        >
          <div className="space-y-3">
            <div className="rounded-xl bg-background/55 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-semibold">One-Time Browser</p>
                  <p className="mt-1 text-[10px] leading-4 text-foreground/40">
                    Open the active RUNYI-governed provider authorization in an isolated one-time browser.
                  </p>
                </div>
                <Badge className="bg-primary/[0.08] text-primary">RUNYI</Badge>
              </div>
              <Button
                type="button"
                onClick={() => void openOneTimeBrowser()}
                disabled={oneTimeBrowserLoading || !supabase}
                className="mt-3 h-9 w-full rounded-xl text-xs font-semibold"
              >
                {oneTimeBrowserLoading ? <LoaderCircle className="size-3.5 animate-spin" /> : <ShieldCheck className="size-3.5" />}
                Open One-Time Browser
              </Button>
              {oneTimeBrowserMessage && (
                <p className="mt-2 rounded-lg bg-foreground/[0.025] px-2.5 py-2 text-[10px] leading-4 text-foreground/48">
                  {oneTimeBrowserMessage}
                </p>
              )}
              {oneTimeBrowserOptions.length > 1 && (
                <div className="mt-2 space-y-1.5">
                  {oneTimeBrowserOptions.map((option) => (
                    <button
                      key={option.setup_id}
                      type="button"
                      disabled={oneTimeBrowserLoading}
                      onClick={() => void openOneTimeBrowser(option.setup_id)}
                      className="flex w-full items-center justify-between gap-3 rounded-lg bg-foreground/[0.025] px-2.5 py-2 text-left hover:bg-foreground/[0.05] disabled:opacity-50"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-[11px] font-semibold">
                          {option.display_name || option.provider_key || "Infrastructure authorization"}
                        </span>
                        <span className="mt-0.5 block truncate text-[9px] text-foreground/38">
                          {option.target_account_ref || option.target_member_email || option.provider_key || "RUNYI provider"}
                        </span>
                      </span>
                      <span className="text-[9px] font-semibold uppercase tracking-wide text-primary">Open</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </SettingsGroup>

        <SettingsGroup
          title="Operations & Governance"
          description="Fleet, readiness, providers, deployments and system knowledge"
          icon={ShieldCheck}
          status={health.connected ? "LIVE" : "OFFLINE"}
          open={groupOpen("operations")}
          hidden={!groupVisible("operations")}
          onToggle={() => toggleGroup("operations")}
        >
          {health.connected && (health.role === "OWNER_ADMIN" || health.role === "ADMIN") ? (
            <EnterpriseControlTowerPanel
              supabase={supabase}
              isOwner={health.role === "OWNER_ADMIN"}
            />
          ) : (
            <p className="rounded-xl bg-background/55 px-3 py-3 text-xs leading-5 text-foreground/42">
              Governance controls are available to Owner Admin and delegated administrators.
            </p>
          )}
        </SettingsGroup>

        <SettingsGroup
          title="Advanced"
          description="Technical project, data, plugin and M4 controls"
          icon={ChevronDown}
          status="TECHNICAL"
          open={groupOpen("advanced")}
          hidden={!groupVisible("advanced")}
          onToggle={() => toggleGroup("advanced")}
        >
          <div className="space-y-3">
            <MemberProjectPanel
              controlSupabase={supabase}
              signedIn={Boolean(signedInEmail && health.connected)}
              isOwner={health.role === "OWNER_ADMIN"}
            />
            <MemberDataAuthPanel
              profile={memberProjectProfile}
              controlSupabase={supabase}
              tenantSupabase={memberSupabase}
              tenantDataSupabase={memberDataSupabase}
              centralEmail={signedInEmail}
              tenantEmail={tenantSignedInEmail}
              onAuthChanged={onTenantAuthChanged}
              onAccessTokenChanged={onTenantAccessTokenChanged}
              onRefreshProfile={onRefreshMemberProjectProfile}
            />
            {health.connected && (
              <PluginConnectionsPanel
                supabase={supabase}
                isOwner={health.role === "OWNER_ADMIN"}
              />
            )}
            {health.connected && (
              <M4ControlTowerPanel
                supabase={supabase}
                isOwner={health.role === "OWNER_ADMIN"}
              />
            )}
          </div>
        </SettingsGroup>
      </div>

      {visibleGroupCount === 0 && visibleNavigationEntries.length === 0 && (
        <div className="rounded-2xl bg-foreground/[0.025] px-4 py-8 text-center">
          <Search className="mx-auto size-5 text-foreground/25" />
          <p className="mt-3 text-sm font-semibold">No Settings section matches</p>
          <p className="mt-1 text-[11px] text-foreground/38">Try users, deployment, Firebase, roles, backend, account or plugins.</p>
        </div>
      )}
    </div>
  );
}

function AdminUsersPanel({ supabase }: { supabase: SupabaseClient | null }) {
  const [emailDraft, setEmailDraft] = useState("");
  const [newAccountRole, setNewAccountRole] = useState<"MEMBER" | "ADMIN">("MEMBER");
  const [searchDraft, setSearchDraft] = useState("");
  const [users, setUsers] = useState<AllowedUser[]>([]);
  const [activeCount, setActiveCount] = useState(0);
  const [maximumUsers, setMaximumUsers] = useState(500);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const filteredUsers = useMemo(() => {
    const query = searchDraft.trim().toLocaleLowerCase();
    if (!query) return users;

    return users.filter((user) => {
      const searchable = [
        user.email,
        user.user_id ?? "",
        user.role,
        user.role === "OWNER_ADMIN" ? "owner admin" : user.role === "ADMIN" ? "administrator admin" : "member",
        user.active ? "active enabled" : "inactive disabled",
        user.joined ? "joined signed in" : "awaiting sign in",
        ...(user.accessible_user_ids ?? []),
        JSON.stringify(user.permissions ?? {}),
      ]
        .join(" ")
        .toLocaleLowerCase();

      return searchable.includes(query);
    });
  }, [searchDraft, users]);

  const loadUsers = useCallback(async () => {
    if (!supabase) {
      setNotice("The administrator session is verified, but the browser client is still reconnecting. Refresh this page once.");
      return;
    }
    setLoading(true);
    const { data, error } = await supabase.rpc("runyi_admin_list_allowed_users");
    if (error) {
      setNotice(error.message);
    } else {
      setUsers(Array.isArray(data?.users) ? data.users as AllowedUser[] : []);
      setActiveCount(Number(data?.active_count ?? 0));
      setMaximumUsers(Number(data?.maximum_users ?? 500));
    }
    setLoading(false);
  }, [supabase]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  async function invokeAccountMutation(payload: Record<string, unknown>) {
    if (!supabase) return "The browser client is still reconnecting.";
    const { data, error } = await supabase.functions.invoke("runyi-account-setup", {
      body: payload,
    });
    if (error) return error.message;
    if (data?.error) return String(data.error);
    return null;
  }

  async function addUsers() {
    if (!supabase) {
      setNotice("The browser client is still reconnecting. Refresh this page once, then try again.");
      return;
    }
    const emails = [...new Set(
      emailDraft
        .split(/[\s,;]+/)
        .map((email) => email.trim().toLowerCase())
        .filter(Boolean),
    )];
    if (!emails.length) {
      setNotice("Enter at least one email address.");
      return;
    }

    setLoading(true);
    setNotice(null);

    const defaultPermissions = newAccountRole === "ADMIN"
      ? {
          "database.read_assigned": true,
          "storage.read_assigned": true,
          "system.read": true,
          "files.read_txt": true,
          "files.write_txt": true,
          "files.read_assigned": true,
        }
      : {
          "files.read_txt": false,
          "files.write_txt": false,
        };

    const failures: string[] = [];
    let saved = 0;
    for (const email of emails) {
      const errorMessage = await invokeAccountMutation({
        operation: "owner_authorize_account",
        email,
        role: newAccountRole,
        permissions: defaultPermissions,
        accessible_user_ids: [],
      });
      if (errorMessage) failures.push(`${email}: ${errorMessage}`);
      else saved += 1;
    }

    if (saved > 0) {
      setEmailDraft("");
      await loadUsers();
    }

    if (failures.length > 0) {
      setNotice(`${saved} account${saved === 1 ? "" : "s"} authorized. ${failures.join(" · ")}`);
    } else {
      setNotice(
        `${saved} ${newAccountRole === "ADMIN" ? "Admin" : "member"} account${saved === 1 ? "" : "s"} authorized. Each person can now request their own email code.`,
      );
    }
    setLoading(false);
  }

  async function setUserAccess(user: AllowedUser, role: "ADMIN" | "MEMBER", fullAccess = false) {
    if (!supabase) return;
    setLoading(true);
    setNotice(null);
    const permissions = role === "ADMIN"
      ? fullAccess
        ? {
            "database.read_all": true,
            "database.admin_tables": true,
            "storage.read_all": true,
            "users.read": true,
            "system.read_all": true,
            "system.manage": true,
            "approvals.decide": true,
            "files.read_txt": true,
            "files.write_txt": true,
            "files.read_all": true,
          }
        : {
            "database.read_assigned": true,
            "storage.read_assigned": true,
            "system.read": true,
            "files.read_txt": true,
            "files.write_txt": true,
            "files.read_assigned": true,
          }
      : {
          "files.read_txt": false,
          "files.write_txt": false,
        };
    const errorMessage = await invokeAccountMutation({
      operation: "owner_set_user_access",
      email: user.email,
      role,
      permissions,
      accessible_user_ids: [],
    });
    setNotice(errorMessage ?? `${user.email} is now ${role === "ADMIN" ? fullAccess ? "a full-access Admin" : "a scoped Admin" : "a Member"}.`);
    if (!errorMessage) await loadUsers();
    setLoading(false);
  }

  async function setUserActive(user: AllowedUser, active: boolean) {
    if (!supabase) {
      setNotice("The browser client is still reconnecting. Refresh this page once, then try again.");
      return;
    }
    setLoading(true);
    setNotice(null);
    const errorMessage = await invokeAccountMutation({
      operation: "admin_set_user_active",
      email: user.email,
      active,
    });
    setNotice(errorMessage ?? `${user.email} is now ${active ? "active" : "inactive"}.`);
    if (!errorMessage) await loadUsers();
    setLoading(false);
  }

  async function setSetupAdmin(user: AllowedUser, enabled: boolean) {
    if (!supabase || user.role !== "ADMIN") return;
    setLoading(true);
    setNotice(null);

    const { data, error } = await supabase.functions.invoke("runyi-account-setup", {
      body: {
        operation: "set_setup_admin",
        admin_email: user.email,
        scope_mode: "ALL_NON_OWNER",
        target_emails: [],
        enabled,
      },
    });
    const errorMessage = error?.message ?? (data?.error ? String(data.error) : null);

    setNotice(
      errorMessage
        ? errorMessage
        : enabled
          ? `${user.email} can now set up and test all non-owner RUNYI accounts.`
          : `Account setup administration revoked for ${user.email}.`,
    );
    if (!errorMessage) await loadUsers();
    setLoading(false);
  }

  async function setTxtAccess(user: AllowedUser, enabled: boolean) {
    if (!supabase || user.role === "OWNER_ADMIN") return;
    setLoading(true);
    setNotice(null);

    const permissions = {
      ...(user.permissions ?? {}),
      "files.read_txt": enabled,
      "files.write_txt": enabled,
    };
    const errorMessage = await invokeAccountMutation({
      operation: "owner_set_user_access",
      email: user.email,
      role: user.role,
      permissions,
      accessible_user_ids: user.role === "ADMIN" ? (user.accessible_user_ids ?? []) : [],
    });
    setNotice(
      errorMessage
        ? errorMessage
        : `TXT file access ${enabled ? "enabled" : "disabled"} for ${user.email}.`,
    );
    if (!errorMessage) await loadUsers();
    setLoading(false);
  }

  return (
    <Card className="min-w-0 overflow-visible border-0 bg-transparent py-0 shadow-none">
      <CardContent className="min-w-0 space-y-3 p-0">
        <div className="flex min-w-0 items-start gap-2.5">
          <div className="grid size-6 shrink-0 place-items-center rounded-md bg-primary/[0.08] text-primary">
            <Users className="size-3" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">Owner Admin access control</p>
            <p className="mt-1 text-xs leading-5 text-foreground/42">
              Authorize accounts and keep user actions inside compact menus so Settings stays clean on phones.
            </p>
            <p className="mt-2 text-xs font-semibold text-foreground/62">{activeCount} of {maximumUsers} active accounts</p>
          </div>
        </div>

        <div className="grid min-w-0 gap-3 sm:grid-cols-[minmax(0,1fr)_180px]">
          <Field label="Account email addresses">
            <Textarea
              value={emailDraft}
              onChange={(event) => setEmailDraft(event.target.value)}
              placeholder={"person1@example.com\nperson2@example.com\nperson3@example.com"}
              className="min-h-20 w-full min-w-0 resize-y border-0 bg-foreground/[0.035] text-[15px] text-foreground shadow-none placeholder:text-[11px] placeholder:text-foreground/28 focus-visible:ring-1 focus-visible:ring-primary/20"
            />
          </Field>
          <Field label="Grant role">
            <Select value={newAccountRole} onValueChange={(value) => setNewAccountRole(value === "ADMIN" ? "ADMIN" : "MEMBER")}>
              <SelectTrigger className="h-9 w-full min-w-0 border-0 bg-foreground/[0.035] text-xs shadow-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MEMBER">Member</SelectItem>
                <SelectItem value="ADMIN">Scoped Admin</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        <p className="text-xs leading-5 text-foreground/42">
          One address per line, or separate addresses with commas or spaces. Duplicate addresses are removed automatically.
        </p>
        <Button onClick={addUsers} disabled={loading || !supabase || !emailDraft.trim()} className="h-9 w-full min-w-0 rounded-xl bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90">
          {loading ? <LoaderCircle className="size-3.5 animate-spin" /> : <UserPlus className="size-3.5" />}
          {newAccountRole === "ADMIN" ? "Add Admin access" : "Authorize member accounts"}
        </Button>

        {notice && <div className="min-w-0 break-words rounded-xl bg-amber-300/[0.08] p-2.5 text-xs leading-5 text-amber-700 dark:text-amber-100">{notice}</div>}

        <div className="min-w-0 space-y-3">
          <Field label="Search users">
            <div className="relative min-w-0">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3 -translate-y-1/2 text-foreground/30" />
              <Input
                type="search"
                inputMode="search"
                autoComplete="off"
                spellCheck={false}
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
                placeholder="Search email, UUID, role, status or permission…"
                aria-label="Search users by any letters, numbers, symbols, email, user ID, role, status or permission"
                className="h-9 w-full min-w-0 border-0 bg-foreground/[0.035] pl-8 pr-3 text-[15px] shadow-none placeholder:text-[11px] placeholder:text-foreground/28 focus-visible:ring-1 focus-visible:ring-primary/20"
              />
            </div>
          </Field>
          <div className="flex min-w-0 items-center justify-between gap-3">
            <p className="min-w-0 text-[11px] leading-4 text-foreground/42">
              Email · UUID · role · status · permissions · letters · numbers · symbols
            </p>
            <p className="shrink-0 text-[11px] text-foreground/42">{filteredUsers.length}/{users.length}</p>
          </div>

          <div className="space-y-2">
            {filteredUsers.map((user) => (
              <div
                key={user.email}
                className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-xl bg-foreground/[0.025] px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold" title={user.email}>{user.email}</p>
                  <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-foreground/42">
                    <span>{user.role === "OWNER_ADMIN" ? "Owner Admin" : user.role === "ADMIN" ? "Administrator" : "Member"}</span>
                    <span>·</span>
                    <span>{user.active ? "Active" : "Inactive"}</span>
                    {user.user_id && (
                      <>
                        <span>·</span>
                        <span className="max-w-[11rem] truncate font-mono" title={user.user_id}>{user.user_id}</span>
                      </>
                    )}
                  </div>
                </div>

                <EnterpriseContextMenu compact label={`Open actions for ${user.email}`}>
                    <div className="border-b border-foreground/8 px-2.5 py-2">
                      <p className="truncate text-xs font-semibold">{user.email}</p>
                      <p className="mt-0.5 text-[10px] text-foreground/40">Account actions</p>
                    </div>

                    {user.role === "ADMIN" && (
                      <button
                        type="button"
                        disabled={loading || !supabase}
                        onClick={(event) => {
                                                    void setSetupAdmin(
                            user,
                            user.permissions?.["account_setup.enabled"] !== true,
                          );
                        }}
                        className="mt-1 flex h-9 w-full items-center rounded-lg px-2.5 text-left text-xs text-foreground/75 hover:bg-foreground/8 disabled:opacity-40"
                      >
                        {user.permissions?.["account_setup.enabled"] === true
                          ? "Revoke Setup Admin"
                          : "Grant Setup Admin"}
                      </button>
                    )}

                    {user.role !== "OWNER_ADMIN" && (
                      <button
                        type="button"
                        disabled={loading || !supabase}
                        onClick={(event) => {
                                                    void setTxtAccess(
                            user,
                            !(user.permissions?.["files.read_txt"] === true && user.permissions?.["files.write_txt"] === true),
                          );
                        }}
                        className="mt-1 flex h-9 w-full items-center rounded-lg px-2.5 text-left text-xs text-foreground/75 hover:bg-foreground/8 disabled:opacity-40"
                      >
                        {user.permissions?.["files.read_txt"] === true && user.permissions?.["files.write_txt"] === true ? "Disable TXT access" : "Enable TXT access"}
                      </button>
                    )}

                    {user.role !== "OWNER_ADMIN" && user.role !== "ADMIN" && (
                      <button
                        type="button"
                        disabled={loading || !supabase}
                        onClick={(event) => {
                                                    void setUserAccess(user, "ADMIN", false);
                        }}
                        className="flex h-9 w-full items-center rounded-lg px-2.5 text-left text-xs text-foreground/75 hover:bg-foreground/8 disabled:opacity-40"
                      >
                        Make Scoped Admin
                      </button>
                    )}

                    {user.role !== "OWNER_ADMIN" && (
                      <button
                        type="button"
                        disabled={loading || !supabase}
                        onClick={(event) => {
                                                    void setUserAccess(user, user.role === "ADMIN" ? "MEMBER" : "ADMIN", user.role !== "ADMIN");
                        }}
                        className="flex h-9 w-full items-center rounded-lg px-2.5 text-left text-xs text-foreground/75 hover:bg-foreground/8 disabled:opacity-40"
                      >
                        {user.role === "ADMIN" ? "Make Member" : "Make Full Admin"}
                      </button>
                    )}

                    <button
                      type="button"
                      disabled={loading || !supabase || user.role === "OWNER_ADMIN"}
                      onClick={(event) => {
                                                void setUserActive(user, !user.active);
                      }}
                      className="flex h-9 w-full items-center rounded-lg px-2.5 text-left text-xs text-foreground/75 hover:bg-foreground/8 disabled:opacity-40"
                    >
                      {user.active ? "Deactivate account" : "Activate account"}
                    </button>
                </EnterpriseContextMenu>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function StatusCard({ label, value, icon: Icon }: { label: string; value: string; icon: LucideIcon }) {
  return <div className="rounded-xl border border-foreground/8 bg-foreground/[0.035] p-4"><Icon className="size-4 text-primary" /><p className="mt-4 text-xs text-foreground/35">{label}</p><p className="mt-1 text-sm font-semibold">{value}</p></div>;
}
